import React, {
	forwardRef,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import classNames from 'classnames';
import { DatePicker as AntDatePicker } from 'antd';
import { Moment } from 'moment';
import { RangePickerProps as AntRangePickerProps } from 'antd/lib/date-picker';
import {
	nowMoment,
	stringifyIfMoment,
	toMoment,
} from '@shared/utils/date';
import {
	DateFormats,
	UtcOffsets,
} from '@shared/utils/constants';
import { range as getRange } from '@shared/utils/array';
import type { UserData } from '@api/utils/sequelize/calculateUserData';
import Select from '@client/components/Select';
import { useAuth } from '@client/lib/auth';
import styles from './styles/DatePicker.module.css';

const truncateMoment = (value: Date | Moment) => toMoment(value)
	.seconds(0)
	.milliseconds(0);

type SingleValue = Date | Moment | null | undefined;
export type RangePickerValue = [SingleValue, SingleValue] | null | undefined;
type InternalTime = [Moment | undefined, Moment | undefined] | undefined;

const valueToInternalTime = (value: RangePickerValue): InternalTime => {
	if (value == null) {
		return undefined;
	}

	return [
		value[0] != null ? toMoment(value[0]).local(true) : undefined,
		value[1] != null ? toMoment(value[1]).local(true) : undefined,
	];
};

const valueToInternalOffset = (value: RangePickerValue): number | null => {
	if (value == null) {
		return null;
	}

	if (value[0] != null) {
		return toMoment(value[0]).utcOffset();
	}

	return null;
};

const internalsToValue = (
	internalTime: InternalTime,
	internalOffset: number | undefined,
	requireOffset: boolean,
): RangePickerValue => {
	if (internalTime === undefined || (internalOffset === undefined && requireOffset)) {
		return undefined;
	}

	const toSingleValue = (t: Moment | undefined): SingleValue => {
		if (t === undefined) {
			return undefined;
		}

		return truncateMoment(t).utcOffset(internalOffset ?? 0, true);
	};

	return [
		toSingleValue(internalTime[0]),
		toSingleValue(internalTime[1]),
	];
};

const valueToMoment = (
	value: RangePickerValue,
): [Moment | undefined, Moment | undefined] | undefined => {
	if (value == null) {
		return undefined;
	}

	return [
		value[0] != null ? toMoment(value[0]) : undefined,
		value[1] != null ? toMoment(value[1]) : undefined,
	];
};

export type RangePickerProps = Omit<AntRangePickerProps, 'value' | 'onChange' | 'defaultValue'> & {
	fullWidth?: boolean;
	rangeHorizontal?: boolean;
	time?: boolean;
	minDate?: Moment;
	maxDate?: Moment;
	onTimezoneChange?: (newOffset: number) => void;
	defaultPickerValue?: Moment;
	disableTimezone?: boolean;
	showTimezone?: boolean;
	defaultUtcOffset?: number;
	showNow?: boolean;
	onBlur?: () => void;
	onChange?: (newValue: RangePickerValue) => void;
	allowClear?: boolean;
	stopPropagation?: boolean;
	forceUtcUpdate?: boolean;
	staticTimezonePicker?: boolean;
	forceOnChange?: boolean;
	wrapperClassName?: string;
	'data-testid'?: string;
	value?: RangePickerValue;
	defaultValue?: RangePickerValue;
};

const RangePicker = forwardRef<any, RangePickerProps>(({
	value,
	onChange,
	fullWidth,
	rangeHorizontal = false,
	time = false,
	className,
	defaultValue,
	inputReadOnly = false,
	minDate: minDateProp,
	maxDate: maxDateProp,
	onTimezoneChange,
	disableTimezone = true,
	stopPropagation = false,
	showTimezone = true,
	onBlur,
	onOpenChange,
	defaultUtcOffset,
	defaultPickerValue,
	forceUtcUpdate = false,
	staticTimezonePicker = false,
	showNow: showNowProp = true,
	forceOnChange = false,
	wrapperClassName,
	...props
}: RangePickerProps, ref: any) => {
	const { userInfo } = useAuth();
	const immediateTimeValue = useRef<[Moment, Moment] | undefined | null>(undefined);
	const internalValue = useRef<RangePickerValue>(undefined);

	const [internalTime, setInternalTime] = useState<InternalTime>(
		valueToInternalTime(value),
	);
	const [internalOffset, setInternalOffset] = useState(
		valueToInternalOffset(value) ?? defaultUtcOffset,
	);
	const [prevInternalOffset, setPrevInternalOffset] = useState<number | undefined>();

	const valueAsString = stringifyIfMoment(value as any);
	const defaultValueAsString = stringifyIfMoment(defaultValue as any);
	const internalTimeAsString = stringifyIfMoment(internalTime as any);

	const defaultValueMoment = useMemo(
		() => valueToMoment(defaultValue),
		[defaultValueAsString],
	);

	const valueMoment = useMemo(
		() => valueToMoment(value),
		[valueAsString],
	);

	useEffect(() => {
		if (internalOffset == null || forceUtcUpdate) {
			setInternalOffset(defaultUtcOffset);
		}

	}, [defaultUtcOffset]);

	useEffect(() => {
		immediateTimeValue.current = undefined;

		const visible = (
			prevInternalOffset === internalOffset &&
			time &&
			internalTime !== null
		);

		setPrevInternalOffset(internalOffset);

		const newValue = internalsToValue(internalTime, internalOffset, time && !disableTimezone);

		const _old = stringifyIfMoment((value ?? internalValue.current) as any) ?? null;
		const _new = stringifyIfMoment(newValue as any) ?? null;

		if (_old === _new) {
			return;
		}

		internalValue.current = newValue;

		visibleChange(visible);

	}, [internalTimeAsString, internalOffset]);

	useEffect(() => {
		const newInternalTime = valueToInternalTime(valueMoment);
		const newInternalOffset = valueToInternalOffset(valueMoment) ?? defaultUtcOffset;

		setInternalTime(newInternalTime);
		setInternalOffset(newInternalOffset);
	}, [valueAsString]);

	const visibleChange = (visible: boolean) => {
		if (
			!visible &&
			internalValue.current !== undefined &&
			typeof onChange === 'function'
		) {
			const newValue = internalValue.current;
			const oldValueString = stringifyIfMoment(value as any);
			const newValueString = stringifyIfMoment(newValue as any);

			if ((newValueString !== oldValueString || newValue == null) && !forceOnChange) {
				onChange(newValue);
			}
		}

		if (typeof onOpenChange === 'function') {
			if (immediateTimeValue.current === undefined) {
				onOpenChange(visible);
			}
		}

		if (!visible && typeof onBlur === 'function') {
			onBlur();
		}
	};

	const onOffsetChange = (newOffset: number) => {
		if (typeof onTimezoneChange === 'function') {
			onTimezoneChange(newOffset);
		}

		setInternalOffset(newOffset);
	};

	const minDate = useMemo(
		() => (minDateProp != null ? truncateMoment(minDateProp) : undefined),
		[minDateProp],
	);
	const maxDate = useMemo(
		() => (maxDateProp != null ? truncateMoment(maxDateProp) : undefined),
		[maxDateProp],
	);

	const disabledDate = useCallback((d: Moment) => {
		if (d == null) {
			return false;
		}

		const dateWithUtc = toMoment(d).utc();

		return (
			(minDate != null && dateWithUtc.isBefore(minDate) && !dateWithUtc.utc().isSame(minDate, 'day')) ||
			(maxDate != null && dateWithUtc.isAfter(maxDate) && !dateWithUtc.isSame(maxDate, 'day'))
		);
	}, [minDate, maxDate]);

	const disabledTime = useCallback((d: Moment | null) => {
		if (d == null) {
			return {};
		}

		if (minDate != null && d.isSame(minDate, 'day')) {
			return {
				disabledHours: () => (minDate.hour() > 0 ? getRange(0, minDate.hour() - 1) : []),
				disabledMinutes: (hour: number) => (hour === minDate.hour() && minDate.minutes() > 0 ?
					getRange(0, minDate.minutes() - 1) :
					[]
				),
			};
		}

		if (maxDate != null && d.isSame(maxDate, 'day')) {
			return {
				disabledHours: () => getRange(maxDate.hour() + 1, 24),
				disabledMinutes: (hour: number) => (hour === maxDate.hour() ?
					getRange(maxDate.minutes() + 1, 60) :
					[]
				),
			};
		}

		return {};
	}, [minDate, maxDate]);

	const dateFormat = useMemo(() => {
		const preferredDateFormat = (userInfo as UserData).dateFormat;
		switch (props.picker) {
			case 'year':
				return 'YYYY';

			case 'month':
				if (preferredDateFormat === DateFormats.YMD) {
					return 'YYYY-MM';
				}

				return 'MM-YYYY';

			default:
				return time ? preferredDateFormat : preferredDateFormat.slice(0, 10);
		}
	}, [props.picker, time, userInfo]);

	const isNowValidDate = (
		(minDate == null || nowMoment().isAfter(minDate)) &&
		(maxDate == null || nowMoment().isBefore(maxDate))
	);

	const showNow = showNowProp != null ?
		showNowProp !== false && isNowValidDate :
		undefined;

	const timeProps = time ? {
		showTime: true as const,
		minuteStep: 1 as const,
	} : {};

	return (
		<div
			className={classNames(styles.wrapper, wrapperClassName, {
				[styles.fullWidth]: fullWidth,
			})}
		>
			{(time && showTimezone) && (
				<Select
					variant={props.variant ?? undefined}
					key={internalOffset}
					className={classNames({
						[styles.timezonePicker]: !staticTimezonePicker,
						[styles.staticTimezonePicker]: staticTimezonePicker,
					})}
					disabled={disableTimezone}
					suffixIcon={disableTimezone ? null : undefined}
					options={UtcOffsets.map((o) => ({
						label: o.offset === 0 && !disableTimezone ? 'UTC+00:00' : o.name,
						value: o.offset,
					}))}
					value={internalOffset ?? 0}
					onChange={(newValue) => onOffsetChange(newValue as number)}
					popupMatchSelectWidth={false}
				/>
			)}
			<AntDatePicker.RangePicker
				data-testid="rangepicker"
				defaultPickerValue={valueToInternalTime(defaultPickerValue as any) as any}
				ref={ref}
				defaultValue={defaultValueMoment as any}
				value={internalTime as any}
				onChange={(newValue) => {
					const typedValue = newValue as InternalTime;

					if (forceOnChange && onChange != null && typeof onChange === 'function') {
						onChange(typedValue as any);
					}

					immediateTimeValue.current = typedValue as any;
					setInternalTime(typedValue);
					internalValue.current = internalsToValue(
						typedValue, internalOffset, time && !disableTimezone,
					);
				}}
				onOpenChange={visibleChange}
				inputReadOnly={inputReadOnly}
				className={classNames(styles.datePicker, className, {
					[styles.range]: true,
					[styles.vertical]: !rangeHorizontal,
				})}
				disabledDate={disabledDate}
				disabledTime={disabledTime as any}
				showNow={showNow}
				panelRender={
					stopPropagation ?
						(panelNode: React.ReactNode) => {
							if (!React.isValidElement(panelNode)) {
								return panelNode;
							}

							return React.cloneElement(panelNode, {
								onClick: (e: React.MouseEvent<HTMLElement>) => {
									e.stopPropagation();
								},
							} as any);
						} :
						undefined
				}
				suffixIcon={null}
				format={dateFormat}
				{...timeProps}
				{...(props as any)}
			/>
		</div>
	);
});

export default RangePicker;
